import { useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import { feature } from "topojson-client";

function latLngToVector3(lat, lng, radius) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

const VIENNA = { lat: 48.2082, lng: 16.3738 };
// 8K NASA Blue Marble texture — sharper at city-level zoom than 4K
const EARTH_8K = "https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-blue-marble.jpg";
const BORDERS_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Module-level cache so React StrictMode + re-mounts don't refetch
let _bordersPromise = null;
function loadBordersTopo() {
  if (!_bordersPromise) {
    _bordersPromise = fetch(BORDERS_URL).then(r => r.json()).catch(e => { _bordersPromise = null; throw e; });
  }
  return _bordersPromise;
}

export default function GlobeScene({ listings = [] }) {
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const listingsRef = useRef(listings);
  listingsRef.current = listings;
  const handlePinClick = useCallback((id) => navigate(`/unterkunft/${id}`), [navigate]);

  // Refs to persist scene state across listings updates without rebuilding WebGL
  const sceneRef = useRef(null);
  const pinGroupRef = useRef(null);
  const pinsArrayRef = useRef([]);
  const radiusRef = useRef(2);
  const rebuildPinsRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.01, 100);
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x050510);
    container.appendChild(renderer.domElement);

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const sun = new THREE.DirectionalLight(0xffffff, 2.0);
    sun.position.set(5, 3, 5);
    scene.add(sun);
    const rimLight = new THREE.DirectionalLight(0x4488ff, 0.5);
    rimLight.position.set(-4, 0, -4);
    scene.add(rimLight);

    // Stars with varying sizes
    const starsGeo = new THREE.BufferGeometry();
    const sv = [], ss = [];
    for (let i = 0; i < 1500; i++) {
      sv.push((Math.random()-0.5)*80, (Math.random()-0.5)*80, (Math.random()-0.5)*80);
      ss.push(Math.random() * 0.06 + 0.01);
    }
    starsGeo.setAttribute("position", new THREE.Float32BufferAttribute(sv, 3));
    starsGeo.setAttribute("size", new THREE.Float32BufferAttribute(ss, 1));
    scene.add(new THREE.Points(starsGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.04, sizeAttenuation: true })));

    // Globe
    const radius = 2;
    const earthGeo = new THREE.SphereGeometry(radius, 128, 128);
    const earthMat = new THREE.MeshPhongMaterial({ color: 0x1a3a6a, specular: 0x222222, shininess: 10 });
    const earth = new THREE.Mesh(earthGeo, earthMat);
    scene.add(earth);

    // Load 8K Blue Marble Earth texture (NASA)
    const loader = new THREE.TextureLoader();
    loader.load(EARTH_8K, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = true;
      earthMat.map = tex;
      earthMat.color = new THREE.Color(0xffffff);
      earthMat.needsUpdate = true;
    });

    // Atmosphere
    const atmosMat = new THREE.ShaderMaterial({
      vertexShader: `varying vec3 vNormal; varying vec3 vPos; void main(){ vNormal=normalize(normalMatrix*normal); vPos=(modelViewMatrix*vec4(position,1.0)).xyz; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader: `varying vec3 vNormal; varying vec3 vPos; void main(){ float rim=1.0-max(0.0,dot(vNormal,normalize(-vPos))); float i=pow(rim,3.0)*0.6; gl_FragColor=vec4(0.4,0.7,1.0,i);}`,
      blending: THREE.AdditiveBlending, side: THREE.BackSide, transparent: true, depthWrite: false,
    });
    scene.add(new THREE.Mesh(new THREE.SphereGeometry(radius * 1.015, 64, 64), atmosMat));

    // Country borders from TopoJSON (module-cached, retried silently on failure)
    loadBordersTopo()
      .then(topo => {
        const countries = feature(topo, topo.objects.countries);
        const borderPoints = [];
        countries.features.forEach(f => {
          const coords = f.geometry.type === "Polygon" ? [f.geometry.coordinates] : f.geometry.coordinates;
          coords.forEach(polygon => {
            polygon.forEach(ring => {
              for (let i = 0; i < ring.length - 1; i++) {
                const p1 = latLngToVector3(ring[i][1], ring[i][0], radius + 0.002);
                const p2 = latLngToVector3(ring[i+1][1], ring[i+1][0], radius + 0.002);
                borderPoints.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
              }
            });
          });
        });
        const borderGeo = new THREE.BufferGeometry();
        borderGeo.setAttribute("position", new THREE.Float32BufferAttribute(borderPoints, 3));
        const borderMat = new THREE.LineBasicMaterial({ color: 0xffd700, opacity: 0.35, transparent: true });
        scene.add(new THREE.LineSegments(borderGeo, borderMat));
      })
      .catch(() => { /* silent: cache cleared so a future mount can retry */ });

    // Wien city marker (always on globe)
    const wienPos = latLngToVector3(VIENNA.lat, VIENNA.lng, radius + 0.003);
    const wienRing = new THREE.Mesh(
      new THREE.RingGeometry(0.015, 0.025, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
    );
    wienRing.position.copy(wienPos);
    wienRing.lookAt(0, 0, 0);
    scene.add(wienRing);

    // Camera positions
    const viennaDir = latLngToVector3(VIENNA.lat, VIENNA.lng, 1).normalize();
    const CAM_START = new THREE.Vector3(0, 1.2, 5.8);
    const CAM_MID = viennaDir.clone().multiplyScalar(3.6); // Europe overview
    const CAM_CLOSE = viennaDir.clone().multiplyScalar(2.5); // Austria/Vienna region close-up (0.5 above surface ≈ 1600 km altitude)
    // Vienna surface point - camera will lookAt this so Vienna fills the frame
    const VIENNA_SURFACE = viennaDir.clone().multiplyScalar(radius);
    const ORIGIN = new THREE.Vector3(0, 0, 0);
    const lookTarget = new THREE.Vector3(0, 0, 0); // dynamic target

    camera.position.copy(CAM_START);
    camera.lookAt(0, 0, 0);

    // Listing pins group (rebuilt when listings change without tearing scene down)
    const pinGroup = new THREE.Group();
    pinGroup.visible = false;
    scene.add(pinGroup);
    const pinsArray = pinsArrayRef.current;
    sceneRef.current = scene;
    pinGroupRef.current = pinGroup;
    radiusRef.current = radius;

    const buildPins = () => {
      // Dispose old pin children
      while (pinGroup.children.length) {
        const c = pinGroup.children.pop();
        if (c.geometry) c.geometry.dispose();
        if (c.material) c.material.dispose();
      }
      pinsArray.length = 0;
      const validListings = listingsRef.current.filter(l => l.lat != null && l.lng != null);
      validListings.forEach((listing, idx) => {
        const angle = (idx / Math.max(validListings.length, 1)) * Math.PI * 2;
        const spread = 0.045; // moderate spread (~5 km at Vienna) so all 5 pins are individually visible at country-overview zoom
        const jLat = listing.lat + Math.cos(angle) * spread;
        const jLng = listing.lng + Math.sin(angle) * spread * 1.4;
        const tipPos = latLngToVector3(jLat, jLng, radius + 0.013);
        const basePos = latLngToVector3(jLat, jLng, radius + 0.002);

        const stemLen = tipPos.distanceTo(basePos);
        const stemGeo = new THREE.CylinderGeometry(0.0006, 0.0006, stemLen, 6);
        const stemMat = new THREE.MeshBasicMaterial({ color: 0xcc0000 });
        const stem = new THREE.Mesh(stemGeo, stemMat);
        stem.position.copy(basePos.clone().add(tipPos).multiplyScalar(0.5));
        stem.lookAt(0, 0, 0);
        stem.rotateX(Math.PI / 2);
        pinGroup.add(stem);

        const headGeo = new THREE.SphereGeometry(0.003, 16, 16);
        const headMat = new THREE.MeshStandardMaterial({
          color: 0xe11d48, emissive: 0xff1f1f, emissiveIntensity: 1.5, roughness: 0.2, metalness: 0.3,
        });
        const head = new THREE.Mesh(headGeo, headMat);
        head.position.copy(tipPos);
        head.userData = { listingId: listing.id, name: listing.name };
        pinGroup.add(head);
        pinsArray.push(head);

        const glowGeo = new THREE.SphereGeometry(0.0055, 12, 12);
        const glowMat = new THREE.MeshBasicMaterial({ color: 0xff3333, transparent: true, opacity: 0.40 });
        const glow = new THREE.Mesh(glowGeo, glowMat);
        glow.position.copy(tipPos);
        glow.userData = { isGlow: true };
        pinGroup.add(glow);
      });
    };
    rebuildPinsRef.current = buildPins;
    buildPins();

    // Animation state
    let phase = 0; // 0=orbit, 1=fly stage1 (to europe), 2=fly stage2 (to vienna), 3=arrived
    let progress = 0;
    let phaseStartT = 0; // wall-clock seconds when current phase began (time-based easing)
    const PHASE1_DURATION = 1.0;
    const PHASE2_DURATION = 1.2;
    let orbitAngle = 0;
    let hoveredPin = null;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const resetAnim = () => { phase = 0; progress = 0; phaseStartT = 0; pinGroup.visible = false; };
    const startAnim = () => { if (phase === 0) { phase = 1; progress = 0; phaseStartT = 0; } };

    // IntersectionObserver - scroll trigger.
    // Only reset if section has been substantially out-of-view for >500ms AND we already arrived,
    // otherwise sub-pixel layout shifts (when async listing cards render below) cause oscillation.
    const section = sectionRef.current || container.parentElement;
    let outOfViewTimer = null;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.2) {
          if (outOfViewTimer) { clearTimeout(outOfViewTimer); outOfViewTimer = null; }
          startAnim();
        } else if (entry.intersectionRatio < 0.05) {
          // Debounce reset; only trigger after 600ms continuously out-of-view AND we have already arrived
          if (outOfViewTimer) clearTimeout(outOfViewTimer);
          outOfViewTimer = setTimeout(() => {
            if (phase === 3) resetAnim();
            outOfViewTimer = null;
          }, 600);
        }
      });
    }, { threshold: [0, 0.05, 0.2, 0.5] });
    if (section) observer.observe(section);

    // Auto-start after 1s
    const autoTimer = setTimeout(startAnim, 1000);

    // Mouse/Touch for pins
    const updateMouse = (cx, cy) => { const r = container.getBoundingClientRect(); mouse.x = ((cx-r.left)/r.width)*2-1; mouse.y = -((cy-r.top)/r.height)*2+1; };
    const hitPins = () => { raycaster.setFromCamera(mouse, camera); return raycaster.intersectObjects(pinsArray); };
    const onMove = (e) => {
      updateMouse(e.clientX, e.clientY);
      if (phase === 3) {
        const hits = hitPins();
        if (hits.length > 0) {
          container.style.cursor = "pointer";
          if (hoveredPin && hoveredPin !== hits[0].object) { hoveredPin.scale.setScalar(1); hoveredPin.material.emissiveIntensity = 1.0; }
          hoveredPin = hits[0].object;
          hoveredPin.scale.setScalar(2.0);
          hoveredPin.material.emissiveIntensity = 2.0;
        } else {
          container.style.cursor = "default";
          if (hoveredPin) { hoveredPin.scale.setScalar(1); hoveredPin.material.emissiveIntensity = 1.0; hoveredPin = null; }
        }
      }
    };
    const onClick = (e) => { updateMouse(e.clientX, e.clientY); if (phase === 3) { const h = hitPins(); if (h.length) handlePinClick(h[0].object.userData.listingId); } };
    const onTouchEnd2 = (e) => { if (phase === 3 && e.changedTouches.length) { updateMouse(e.changedTouches[0].clientX, e.changedTouches[0].clientY); const h = hitPins(); if (h.length) handlePinClick(h[0].object.userData.listingId); } };

    container.addEventListener("mousemove", onMove);
    container.addEventListener("click", onClick);
    container.addEventListener("touchend", onTouchEnd2, { passive: true });

    // Easing
    const easeInOut = (t) => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    // Render loop
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const t = Date.now() * 0.001;

      if (phase === 0) {
        // Gentle orbit
        orbitAngle += 0.004;
        camera.position.x = Math.sin(orbitAngle) * 5.8;
        camera.position.z = Math.cos(orbitAngle) * 5.8;
        camera.position.y = 1.0 + Math.sin(t * 0.2) * 0.3;
        lookTarget.set(0, 0, 0);
        camera.lookAt(lookTarget);
      } else if (phase === 1) {
        // Stage 1: Fly to Europe overview - time-based, frame-rate independent
        if (phaseStartT === 0) phaseStartT = t;
        progress = Math.min(1, (t - phaseStartT) / PHASE1_DURATION);
        const e = easeInOut(progress);
        const startPos = new THREE.Vector3(Math.sin(orbitAngle) * 5.8, 1.0, Math.cos(orbitAngle) * 5.8);
        camera.position.lerpVectors(startPos, CAM_MID, e);
        lookTarget.set(0, 0, 0);
        camera.lookAt(lookTarget);
        if (progress >= 1) { phase = 2; progress = 0; phaseStartT = 0; }
      } else if (phase === 2) {
        // Stage 2: Zoom into Vienna - time-based, lookAt smoothly transitions to Vienna surface
        if (phaseStartT === 0) phaseStartT = t;
        progress = Math.min(1, (t - phaseStartT) / PHASE2_DURATION);
        const e = easeOut(progress);
        camera.position.lerpVectors(CAM_MID, CAM_CLOSE, e);
        lookTarget.lerpVectors(ORIGIN, VIENNA_SURFACE, e);
        camera.lookAt(lookTarget);
        // Show pins at 50% so they're visible during the final approach
        if (progress > 0.5 && !pinGroup.visible) pinGroup.visible = true;
        if (progress >= 1) { phase = 3; phaseStartT = 0; }
      } else if (phase === 3) {
        // Arrived - very subtle breathing motion (camera at distance 2.5, surface 0.5 away)
        camera.position.copy(CAM_CLOSE);
        camera.position.x += Math.sin(t * 0.3) * 0.003;
        camera.position.y += Math.cos(t * 0.2) * 0.002;
        lookTarget.copy(VIENNA_SURFACE);
        camera.lookAt(lookTarget);
        // Pulse pin glows
        pinGroup.children.forEach(c => {
          if (c.userData?.isGlow) {
            const s = 1 + Math.sin(t * 2.5) * 0.3;
            c.scale.setScalar(s);
            c.material.opacity = 0.25 + Math.sin(t * 2.5) * 0.1;
          }
        });
      }

      // Wien ring pulse
      if (wienRing) {
        const ws = 1 + Math.sin(t * 1.5) * 0.15;
        wienRing.scale.setScalar(ws);
      }

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => { camera.aspect = container.clientWidth / container.clientHeight; camera.updateProjectionMatrix(); renderer.setSize(container.clientWidth, container.clientHeight); };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      clearTimeout(autoTimer);
      if (outOfViewTimer) clearTimeout(outOfViewTimer);
      observer.disconnect();
      window.removeEventListener("resize", onResize);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("click", onClick);
      container.removeEventListener("touchend", onTouchEnd2);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [handlePinClick]);

  // Rebuild pins when listings change - WITHOUT tearing down the WebGL scene/animation
  useEffect(() => {
    if (rebuildPinsRef.current) {
      rebuildPinsRef.current();
    }
  }, [listings]);

  return (
    <div ref={sectionRef} data-testid="globe-section" className="w-full h-[550px] md:h-[700px] relative overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #0a0a2a 0%, #050510 100%)" }}>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAFAFA] to-transparent z-10 pointer-events-none" />
      <div className="absolute top-8 left-0 right-0 z-10 text-center pointer-events-none px-6">
        <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/30 mb-2">Wien, Österreich</p>
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Unsere Standorte</h2>
      </div>
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}
